import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ModelContent } from 'pages/Home/ModelContent';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getUserJobs } from 'store/actions/userData';
import { ModelContext } from 'pages/Home/ModelContext';
import { useCookies } from 'react-cookie';
import { hasPendingJob } from 'utils/jobSockets';
import { JOB_STATUSES } from 'utils/enums';
import { JobReadyDialog } from './JobReadyDialog';

const POLL_INTERVAL_MS = 4000;

const Jobs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [ cookies ] = useCookies([ 'userAuth' ]);
  const token = cookies.archvision_token;
  const userDataStore = useSelector(state => state.userData);
  const userId = userDataStore?.data?.userId;
  const userJobs = useMemo(() => userDataStore.userJobs, [ userDataStore.userJobs ]);
  const prevJobsRef = useRef([]);
  const deletedGuidsRef = useRef(new Set());
  const [ readyJob, setReadyJob ] = useState(null);

  const hasPending = !!hasPendingJob(userJobs?.filteredResults?.[0]?.models);

  useEffect(() => {
    if (!hasPending || !token || !userId) return;

    const intervalId = setInterval(() => {
      dispatch(getUserJobs({ token, userId }));
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [ hasPending, token, userId ]);

  const handleGoToFiles = () => {
    setReadyJob(null);
    navigate('/my-files');
  };

  useEffect(() => {
    const currentJobs = userJobs?.filteredResults?.[0]?.models ?? [];
    const previousJobs = prevJobsRef.current;
    prevJobsRef.current = currentJobs;

    if (!previousJobs.length) return;

    const { QUEUED, PROCESSING, WAITING_FOR_FILE } = JOB_STATUSES;
    const isPending = status =>
      status === QUEUED || status === PROCESSING || status === WAITING_FOR_FILE;
    const currentGuids = new Set(currentJobs.map(job => job.job_guid));

    const completedJob = previousJobs.find(
      job => isPending(job.status) && !currentGuids.has(job.job_guid) && !deletedGuidsRef.current.has(job.job_guid),
    );

    if (completedJob) {
      setReadyJob(completedJob);
    }
  }, [ userJobs ]);

  const contextValue = {
    getModels: () => dispatch(getUserJobs({ token, userId })),
    onBeforeDelete: ({ job_guid: jobGuid }) => deletedGuidsRef.current.add(jobGuid),
    workingData: userJobs,
    sortingOptions: [ 'newest first', 'oldest first' ],
    pageTitle: 'My Jobs',
    searchParam: 'status',
    searchCategoryParam: 'status',
  };

  return (
    <>
      <JobReadyDialog
        job={readyJob}
        onGoToFiles={handleGoToFiles}
        onClose={() => setReadyJob(null)}
      />

      <ModelContext.Provider value={contextValue}>
        <ModelContent />
      </ModelContext.Provider>
    </>
  );
};

export { Jobs };
